import React, { useMemo, useState } from "react";
import { createRoot } from "react-dom/client";
import {
  AlertTriangle,
  Braces,
  CheckCircle2,
  Clipboard,
  Code2,
  Copy,
  ExternalLink,
  FileJson2,
  Globe2,
  Languages,
  Layers3,
  Link2,
  ListChecks,
  PackageCheck,
  RefreshCw,
  SearchCheck,
  Sparkles,
  Store,
  Type,
  XCircle
} from "lucide-react";
import {
  buildReadiness,
  buildSchema,
  getKind,
  kindDefs,
  rawJson,
  scriptTag,
  starterDrafts,
  type Draft,
  type FieldDef,
  type KindDef,
  type Level,
  type Readiness,
  type SchemaKind
} from "./schemaEngine";
import "./styles.css";

type Language = "en" | "ko";
type OutputTab = "script" | "json" | "checklist";

const text = {
  en: {
    product: "SchemaPilot Studio",
    title: "JSON-LD markup with field readiness",
    subtitle: "Generate Article, Product, LocalBusiness, Organization, and FAQPage schema with copy-ready output and official testing links.",
    schemaType: "Schema type",
    fields: "Fields",
    readiness: "Readiness",
    output: "Output",
    graph: "Entity graph",
    links: "Official checks",
    sample: "Reset sample",
    pretty: "Pretty output",
    copy: "Copy",
    copied: "Copied",
    required: "Required",
    recommended: "Recommended",
    imageAlt: "SchemaPilot structured data workbench illustration",
    scopeTitle: "Eligibility note",
    scopeBody: "Valid structured data can make a page eligible for rich results, but Google does not guarantee appearance in Search.",
    script: "Script",
    json: "JSON",
    checklist: "Checklist"
  },
  ko: {
    product: "SchemaPilot Studio",
    title: "필드 준비도를 보여주는 JSON-LD 생성기",
    subtitle: "Article, Product, LocalBusiness, Organization, FAQPage 스키마를 만들고 복사 가능한 출력과 공식 테스트 링크를 제공합니다.",
    schemaType: "스키마 타입",
    fields: "필드",
    readiness: "준비도",
    output: "출력",
    graph: "엔티티 그래프",
    links: "공식 검사",
    sample: "샘플 초기화",
    pretty: "보기 좋은 출력",
    copy: "복사",
    copied: "복사됨",
    required: "필수",
    recommended: "권장",
    imageAlt: "SchemaPilot 구조화 데이터 작업대 일러스트",
    scopeTitle: "노출 기준",
    scopeBody: "올바른 구조화 데이터는 rich result 자격을 도울 수 있지만 Google 검색 노출을 보장하지는 않습니다.",
    script: "스크립트",
    json: "JSON",
    checklist: "체크리스트"
  }
};

const kindIcons: Record<SchemaKind, React.ReactNode> = {
  Article: <Type size={18} />,
  Product: <PackageCheck size={18} />,
  LocalBusiness: <Store size={18} />,
  Organization: <Layers3 size={18} />,
  FAQPage: <SearchCheck size={18} />
};

function App() {
  const [language, setLanguage] = useState<Language>("en");
  const [kind, setKind] = useState<SchemaKind>("Article");
  const [draft, setDraft] = useState<Draft>(starterDrafts.Article);
  const [pretty, setPretty] = useState(true);
  const [activeTab, setActiveTab] = useState<OutputTab>("script");
  const t = text[language];
  const kindDef = getKind(kind);
  const schema = useMemo(() => buildSchema(kind, draft), [kind, draft]);
  const readiness = useMemo(() => buildReadiness(kindDef, draft), [kindDef, draft]);
  const scriptOutput = useMemo(() => scriptTag(schema, pretty), [schema, pretty]);
  const jsonOutput = useMemo(() => rawJson(schema, pretty), [schema, pretty]);
  const checklistOutput = readiness.checks.map((check) => `${check.level.toUpperCase()} - ${check.title}: ${check.detail}`).join("\n");
  const output = activeTab === "script" ? scriptOutput : activeTab === "json" ? jsonOutput : checklistOutput;

  function selectKind(nextKind: SchemaKind) {
    setKind(nextKind);
    setDraft(starterDrafts[nextKind]);
  }

  function updateField(key: string, value: string) {
    setDraft((current) => ({ ...current, [key]: value }));
  }

  return (
    <main className="studio-shell">
      <section className="studio-frame">
        <header className="topbar">
          <a data-google-vignette="false" className="brand" href="/" aria-label="SchemaPilot Studio home">
            <span className="brand-mark">
              <Braces size={22} />
            </span>
            <span>{t.product}</span>
          </a>
          <nav aria-label="Site links">
            <a data-google-vignette="false" href="/about.html">About</a>
            <a data-google-vignette="false" href="/privacy.html">Privacy</a>
          </nav>
          <button className="language-button" type="button" onClick={() => setLanguage(language === "en" ? "ko" : "en")}>
            <Languages size={17} />
            {language.toUpperCase()}
          </button>
        </header>

        <section className="hero-workbench">
          <div className="hero-copy">
            <p className="eyebrow">
              <FileJson2 size={16} />
              Schema.org / JSON-LD / Rich results
            </p>
            <h1>{t.title}</h1>
            <p>{t.subtitle}</p>
          </div>
          <figure className="hero-visual">
            <img src="/schemapilot-workbench.png" alt={t.imageAlt} />
          </figure>
        </section>

        <section className="summary-strip" aria-label="Schema summary">
          <ScoreCard readiness={readiness} label={t.readiness} />
          <MetricCard icon={<ListChecks size={18} />} label={t.required} value={`${readiness.requiredDone}/${readiness.requiredTotal}`} level={readiness.requiredDone === readiness.requiredTotal ? "pass" : "fail"} />
          <MetricCard icon={<Sparkles size={18} />} label={t.recommended} value={`${readiness.recommendedDone}/${readiness.recommendedTotal}`} level={readiness.recommendedDone === readiness.recommendedTotal ? "pass" : "warn"} />
          <MetricCard icon={<Globe2 size={18} />} label="Support" value={kindDef.support} level={kindDef.support === "Limited surface" ? "warn" : "pass"} />
        </section>

        <section className="schema-workspace">
          <aside className="kind-panel">
            <PanelHeader icon={<Layers3 size={18} />} title={t.schemaType} />
            <div className="kind-list">
              {kindDefs.map((item) => (
                <button className={item.kind === kind ? "active" : ""} key={item.kind} type="button" onClick={() => selectKind(item.kind)}>
                  <span>{kindIcons[item.kind]}</span>
                  <strong>{item.label}</strong>
                  <em>{item.support}</em>
                </button>
              ))}
            </div>
            <div className="scope-note">
              <strong>{t.scopeTitle}</strong>
              <p>{t.scopeBody}</p>
            </div>
          </aside>

          <section className="form-panel">
            <PanelHeader icon={<Clipboard size={18} />} title={`${t.fields}: ${kindDef.label}`} />
            <p className="type-description">{kindDef.description}</p>
            <div className="field-grid">
              {kindDef.fields.map((field) => (
                <FieldControl key={field.key} field={field} value={draft[field.key] ?? ""} onChange={updateField} requiredLabel={t.required} recommendedLabel={t.recommended} />
              ))}
            </div>
            <div className="form-actions">
              <button type="button" className="secondary-button" onClick={() => setDraft(starterDrafts[kind])}>
                <RefreshCw size={16} />
                {t.sample}
              </button>
              <label className="toggle">
                <input type="checkbox" checked={pretty} onChange={(event) => setPretty(event.target.checked)} />
                <span>{t.pretty}</span>
              </label>
            </div>
          </section>

          <aside className="output-column">
            <section className="output-panel">
              <PanelHeader icon={<Code2 size={18} />} title={t.output} />
              <div className="tab-row" role="tablist" aria-label="Output format">
                <button className={activeTab === "script" ? "active" : ""} type="button" onClick={() => setActiveTab("script")}>
                  {t.script}
                </button>
                <button className={activeTab === "json" ? "active" : ""} type="button" onClick={() => setActiveTab("json")}>
                  {t.json}
                </button>
                <button className={activeTab === "checklist" ? "active" : ""} type="button" onClick={() => setActiveTab("checklist")}>
                  {t.checklist}
                </button>
              </div>
              <div className="code-box">
                <pre>{output}</pre>
                <CopyButton value={output} copyLabel={t.copy} copiedLabel={t.copied} />
              </div>
            </section>

            <section className="graph-panel">
              <PanelHeader icon={<Braces size={18} />} title={t.graph} />
              <EntityGraph schema={schema} kindDef={kindDef} />
            </section>

            <section className="checks-panel">
              <PanelHeader icon={<CheckCircle2 size={18} />} title={t.links} />
              <ReadinessList checks={readiness.checks} />
              <div className="official-links">
                <a data-google-vignette="false" href="https://search.google.com/test/rich-results" target="_blank" rel="noreferrer">
                  Rich Results Test
                  <ExternalLink size={14} />
                </a>
                <a data-google-vignette="false" href="https://validator.schema.org/" target="_blank" rel="noreferrer">
                  Schema.org validator
                  <ExternalLink size={14} />
                </a>
                <a data-google-vignette="false" href={kindDef.googleDoc} target="_blank" rel="noreferrer">
                  Google docs
                  <ExternalLink size={14} />
                </a>
              </div>
            </section>
          </aside>
        </section>
      </section>
    </main>
  );
}

function FieldControl({
  field,
  value,
  onChange,
  requiredLabel,
  recommendedLabel
}: {
  field: FieldDef;
  value: string;
  onChange: (key: string, value: string) => void;
  requiredLabel: string;
  recommendedLabel: string;
}) {
  const badge = field.required ? requiredLabel : field.recommended ? recommendedLabel : "";

  return (
    <label className={field.input === "textarea" ? "field wide" : "field"}>
      <span>
        {field.label}
        {badge ? <em className={field.required ? "required" : "recommended"}>{badge}</em> : null}
      </span>
      {field.input === "textarea" ? (
        <textarea value={value} placeholder={field.placeholder} onChange={(event) => onChange(field.key, event.target.value)} />
      ) : field.input === "select" ? (
        <select value={value} onChange={(event) => onChange(field.key, event.target.value)}>
          {(field.options ?? []).map((option) => (
            <option value={option} key={option}>
              {option}
            </option>
          ))}
        </select>
      ) : (
        <input value={value} type={field.input} placeholder={field.placeholder} onChange={(event) => onChange(field.key, event.target.value)} />
      )}
    </label>
  );
}

function PanelHeader({ icon, title }: { icon: React.ReactNode; title: string }) {
  return (
    <header className="panel-header">
      <div>
        {icon}
        <h2>{title}</h2>
      </div>
    </header>
  );
}

function ScoreCard({ readiness, label }: { readiness: Readiness; label: string }) {
  const angle = Math.max(0, Math.min(100, readiness.score)) * 3.6;
  return (
    <article className="score-card">
      <div className="score-ring" style={{ "--angle": `${angle}deg` } as React.CSSProperties}>
        <span>{readiness.score}</span>
      </div>
      <div>
        <p>{label}</p>
        <strong>{readiness.label}</strong>
        <span>{readiness.requiredDone}/{readiness.requiredTotal} required fields complete</span>
      </div>
    </article>
  );
}

function MetricCard({ icon, label, value, level }: { icon: React.ReactNode; label: string; value: string; level: Level }) {
  return (
    <article className={`metric-card ${level}`}>
      <div>{icon}</div>
      <p>{label}</p>
      <strong>{value}</strong>
    </article>
  );
}

function EntityGraph({ schema, kindDef }: { schema: Record<string, unknown>; kindDef: KindDef }) {
  const nodes = Object.entries(schema)
    .filter(([key]) => !key.startsWith("@"))
    .slice(0, 8);

  return (
    <div className="entity-graph">
      <div className="root-node">
        <Braces size={20} />
        <strong>{kindDef.label}</strong>
      </div>
      <div className="node-grid">
        {nodes.map(([key, value]) => (
          <article key={key}>
            <Link2 size={14} />
            <span>{key}</span>
            <em>{Array.isArray(value) ? `${value.length} item` : typeof value === "object" ? "object" : "value"}</em>
          </article>
        ))}
      </div>
    </div>
  );
}

function ReadinessList({ checks }: { checks: Readiness["checks"] }) {
  return (
    <div className="readiness-list">
      {checks.map((check) => (
        <article className={`readiness-item ${check.level}`} key={check.key}>
          <LevelIcon level={check.level} />
          <div>
            <strong>{check.title}</strong>
            <span>{check.detail}</span>
          </div>
        </article>
      ))}
    </div>
  );
}

function LevelIcon({ level }: { level: Level }) {
  if (level === "pass") return <CheckCircle2 className="level pass" size={18} />;
  if (level === "fail") return <XCircle className="level fail" size={18} />;
  if (level === "warn") return <AlertTriangle className="level warn" size={18} />;
  return <Sparkles className="level info" size={18} />;
}

function CopyButton({ value, copyLabel, copiedLabel }: { value: string; copyLabel: string; copiedLabel: string }) {
  const [copied, setCopied] = useState(false);

  async function copy() {
    await navigator.clipboard.writeText(value);
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1300);
  }

  return (
    <button className="copy-button" type="button" onClick={copy}>
      <Copy size={15} />
      {copied ? copiedLabel : copyLabel}
    </button>
  );
}

createRoot(document.getElementById("root")!).render(<App />);
